import throttle from '../utils/throttle';

/**
 * 🪤 THE `timeupdate` HAZARD, WHICH IS THE WHOLE REASON THIS FILE EXISTS.
 *
 * D-240 wants `aria-valuetext` on the three scrubbers so the announced position is a
 * time instead of a bare percentage. The naive form of that fix is unusable, and the
 * plan flagged it before it was written (§4 A5): screen readers announce value changes
 * on a FOCUSED slider, `position` re-binds on every `timeupdate`, and Blink fires
 * `timeupdate` roughly four times a second. Leave focus on the scrubber — which is
 * exactly what a keyboard user who just seeked has done — and a time is read aloud
 * continuously. The control becomes unusable BECAUSE it was labelled.
 *
 * 🔑 The integer floor is not enough on its own, and it is worth saying why, because
 * it looks like it should be. `Player.onTimeUpdate` already does `parseInt`, so
 * `timeElapsed` changes about once a second rather than four times — but `seekPos` is
 * a float and changes on every event, and once a second is still once a second. Both
 * v4 and v3 recompute from `timeElapsed`/`timeRemaining`, so the ceiling without a
 * throttle is ~60 announcements a minute.
 *
 * So the commit into `valueText` is rate-limited, leading-edge, and the host's own
 * `spokenPosition` computed stays pure — the throttle governs when the string is
 * PUBLISHED to the attribute, not how it is built.
 *
 * 🔴 A USER SEEK IS ANNOUNCED IMMEDIATELY, AND THE OBVIOUS PLACEMENT FOR THAT IS
 * WRONG. The tempting version calls a flush from the `@input` handler. But at that
 * moment nothing downstream has moved: the handler only EMITS `seeking`, and the
 * elapsed/remaining props it will be judged on arrive later, after `Player.onSeeking`
 * writes `currentTime` and the element fires its own `timeupdate`. Flushing there
 * publishes the position the user has just left — a control that answers an arrow
 * press by reading out the previous value is worse than one that says nothing.
 *
 * So the seek only ARMS an announcement (`markUserSeek`), and the watcher spends it on
 * the next `spokenPosition` change, bypassing the window. Feedback is immediate,
 * correct, and still costs one announcement rather than a stream.
 *
 * 🪤 `beforeUnmount` cancels. A pending trailing timer would otherwise write into a
 * torn-down instance — the same shape as the `release()`-never-called leak A4 fixed in
 * `Utils/Modal`. There is a mutant for it.
 *
 * The host supplies one computed, `spokenPosition`, returning the string to announce
 * or `null` for "say nothing"; everything else is here so the three scrubbers cannot
 * drift apart the way `Controls/PlayPause` did (D-234 was the same code, broken the
 * same way, in two files).
 */

// 5 s, not 1 s and not 30 s. Under about 3 s the announcements still overlap each
// other on a slow voice; much over 5 s and a user arrowing through a long episode
// stops getting feedback that the seek landed. The seek path does not wait for it.
export const VALUE_TEXT_THROTTLE_MS = 5000;

export default {
  data() {
    return {
      valueText: null,
      // Not in `data` for reactivity's sake — nothing renders it — but keeping it
      // here rather than on `this` means the spec can read it, and a stray flag is
      // easier to see in a component dump than an undeclared instance property.
      pendingSeekAnnouncement: false,
    };
  },

  created() {
    this.commitValueText = throttle(() => {
      this.valueText = this.spokenPosition;
    }, VALUE_TEXT_THROTTLE_MS);

    // Seed THROUGH the throttle rather than assigning directly, so the first window
    // opens here. Assigning directly would leave the window shut, and the first
    // `timeupdate` a second later would then announce again immediately — two
    // announcements in the first second, which is the thing being prevented.
    this.commitValueText();
  },

  watch: {
    spokenPosition() {
      if (this.pendingSeekAnnouncement) {
        this.pendingSeekAnnouncement = false;
        this.commitValueText.flush();
        return;
      }

      this.commitValueText();
    },
  },

  beforeUnmount() {
    this.commitValueText.cancel();
  },

  methods: {
    /**
     * Arm an immediate announcement for the next position change. Called from the
     * scrubber's own `@input` handler — see the 🔴 above for why it does not publish
     * anything itself.
     */
    markUserSeek() {
      this.pendingSeekAnnouncement = true;
    },
  },
};
